/**
 * Workspace Subscription Tab - 구독 이력 및 관리
 * 
 * - 구독 이력 테이블
 * - 새 구독 추가 (즉시/예약 적용)
 */

import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { History, Loader2, Save, CreditCard, Calendar, Pencil, XCircle, FileText } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../../../components/ui/dialog';
import { cn } from '../../../lib/utils';
import { WorkspaceTier } from '../../../models/local/Types';
import { Tier, TIERS_COL } from '../../../models/Tier';
import { Promotion, PROMOTIONS_COL, isPromotionValid, applyPromotion, formatPromotionDiscount } from '../../../models/Promotion';
import { SubscriptionData, defaultSubscriptionData, calculateDiscountedPrice } from '../../../models/SubscriptionData';
import { Fire } from '../../../services/FireClient';
import { getAdminFunc } from '../../../services/SuperAdminFirebase';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { IconMoreHorizontal, IconPlus } from '../../components/FigmaIcons';

interface SubscriptionRecord {
    id: string;
    planId: string;
    status: 'active' | 'scheduled' | 'canceled' | 'expired';
    startDate: Date;
    endDate: Date | null;
    scheduledFor: Date | null;
    baseAmount: number;
    amount: number;
    currency: string;
    discount: {
        type: string;
        value: number;
        reason?: string;
    } | null;
    reason: string;
    createdAt: Date;
}

interface Props {
    workspaceId: string;
    onSubscriptionChanged?: () => void;
}



const WorkspaceSubscriptionTab: React.FC<Props> = ({ workspaceId, onSubscriptionChanged }) => {
    const { i18n } = useLingui();

    // Data
    const [subscriptions, setSubscriptions] = useState<SubscriptionRecord[]>([]);
    const [loading, setLoading] = useState(true);

    // Dialog state
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editedSubscription, setEditedSubscription] = useState<SubscriptionData>(defaultSubscriptionData());

    // Form options
    const [changeReason, setChangeReason] = useState('');
    const [createInvoice, setCreateInvoice] = useState(true);
    const [applyImmediately, setApplyImmediately] = useState(true);

    // Tiers from Firestore
    const [tiers, setTiers] = useState<Tier[]>([]);

    // Promotions from Firestore
    const [promotions, setPromotions] = useState<Promotion[]>([]);
    const [selectedPromotion, setSelectedPromotion] = useState<Promotion | null>(null);

    // Load subscriptions, tiers, and promotions on mount
    useEffect(() => {
        loadSubscriptions();
        loadTiers();
        loadPromotions();
    }, [workspaceId]);

    const loadSubscriptions = async () => {
        setLoading(true);
        try {
            const q = query(
                collection(Fire.store, 'workspaces', workspaceId, 'subscriptions'),
                orderBy('createdAt', 'desc')
            );
            const snap = await getDocs(q);
            const list = snap.docs.map(d => {
                const data = d.data();
                return {
                    id: d.id,
                    planId: data.planId,
                    status: data.status,
                    startDate: data.startDate?.toDate?.() || new Date(),
                    endDate: data.endDate?.toDate?.() || null,
                    scheduledFor: data.scheduledFor?.toDate?.() || null,
                    baseAmount: data.baseAmount || 0,
                    amount: data.amount || 0,
                    currency: data.currency || 'JPY',
                    discount: data.discount || null,
                    reason: data.reason || '',
                    createdAt: data.createdAt?.toDate?.() || new Date(),
                } as SubscriptionRecord;
            });
            setSubscriptions(list);
        } catch (e) {
            console.error('Failed to load subscriptions', e);
        } finally {
            setLoading(false);
        }
    };

    const loadTiers = async () => {
        try {
            const q = query(
                collection(Fire.store, TIERS_COL),
                orderBy('displayOrder', 'asc')
            );
            const snap = await getDocs(q);
            const tierList = snap.docs
                .map(d => ({ id: d.id, ...d.data() } as Tier))
                .filter(t => t.isActive);
            setTiers(tierList);
        } catch (e) {
            console.error('Failed to load tiers', e);
        }
    };

    const loadPromotions = async () => {
        try {
            const snap = await getDocs(collection(Fire.store, PROMOTIONS_COL));
            const promoList = snap.docs
                .map(d => {
                    const data = d.data();
                    return {
                        id: d.id,
                        ...data,
                        validFrom: data.validFrom?.toDate?.() || new Date(),
                        validUntil: data.validUntil?.toDate?.() || new Date(),
                        createdAt: data.createdAt?.toDate?.() || new Date(),
                        updatedAt: data.updatedAt?.toDate?.() || new Date(),
                    } as Promotion;
                })
                .filter(p => p.status === 'active');
            setPromotions(promoList);
        } catch (e) {
            console.error('Failed to load promotions', e);
        }
    };

    const handleSaveSubscription = async () => {
        if (!changeReason.trim()) {
            alert('변경 이유를 입력해주세요.');
            return;
        }

        setSaving(true);
        try {
            const createSubscriptionApi = getAdminFunc('createSubscription');

            const endDateISO = editedSubscription.endDate instanceof Date
                ? editedSubscription.endDate.toISOString()
                : null;

            const result = await createSubscriptionApi({
                workspaceId,
                subscriptionData: {
                    planId: editedSubscription.planId,
                    endDate: endDateISO,
                    paymentMethod: editedSubscription.paymentMethod || 'invoice_transfer',
                    billingCycle: editedSubscription.billingCycle || 'monthly',
                },
                pricing: {
                    baseAmount: editedSubscription.baseAmount || 0,
                    amount: editedSubscription.amount || 0,
                    currency: editedSubscription.currency || 'JPY',
                    discount: editedSubscription.discount || null,
                    notes: editedSubscription.notes || '',
                },
                reason: changeReason,
                createInvoice: createInvoice,
                applyImmediately: applyImmediately
            });

            const data = result.data as { message: string };
            alert(data.message || '구독이 성공적으로 추가되었습니다.');

            setIsDialogOpen(false);
            setChangeReason('');
            setApplyImmediately(true);
            setSelectedPromotion(null);
            loadSubscriptions();
            onSubscriptionChanged?.(); // Notify parent to refresh other tabs
        } catch (error: any) {
            console.error('Failed to create subscription:', error);
            alert('구독 생성에 실패했습니다: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    // 구독 취소
    const handleCancelSubscription = async (sub: SubscriptionRecord) => {
        if (sub.status === 'active') {
            const confirm = window.confirm(t(i18n)`Do you want to cancel the current active subscription? It will switch to the FREE plan.`);
            if (!confirm) return;

            // Active 구독은 FREE로 전환
            try {
                const createSubscriptionApi = getAdminFunc('createSubscription');
                await createSubscriptionApi({
                    workspaceId,
                    subscriptionData: {
                        planId: 'FREE',
                        endDate: null,
                        paymentMethod: 'none',
                        billingCycle: 'monthly',
                    },
                    pricing: {
                        baseAmount: 0,
                        amount: 0,
                        currency: 'JPY',
                        discount: null,
                    },
                    reason: `기존 구독(${sub.planId}) 취소로 인한 FREE 전환`,
                    createInvoice: false,
                    applyImmediately: true,
                });

                alert(t(i18n)`Subscription cancelled and switched to FREE plan.`);
                loadSubscriptions();
                onSubscriptionChanged?.();
            } catch (error: any) {
                console.error('Failed to cancel subscription:', error);
                alert(t(i18n)`Failed to cancel subscription: ` + error.message);
            }
        } else if (sub.status === 'scheduled') {
            const confirm = window.confirm(t(i18n)`Do you want to cancel the scheduled subscription?`);
            if (!confirm) return;

            try {
                const cancelSubscriptionApi = getAdminFunc('cancelSubscription');
                await cancelSubscriptionApi({
                    workspaceId,
                    subscriptionId: sub.id,
                    reason: '관리자에 의한 예약 취소',
                });

                alert(t(i18n)`Scheduled subscription has been cancelled.`);
                loadSubscriptions();
                onSubscriptionChanged?.();
            } catch (error: any) {
                console.error('Failed to cancel scheduled subscription:', error);
                alert(t(i18n)`Failed to cancel subscription: ` + error.message);
            }
        }
    };

    // 청구서 생성 (기존 구독에 대해)
    const handleCreateInvoice = async (sub: SubscriptionRecord) => {
        if (sub.amount <= 0) {
            alert(t(i18n)`Cannot create invoice for a subscription with zero amount.`);
            return;
        }

        try {
            const { collection, getDocs, query, where, addDoc, serverTimestamp } = await import('firebase/firestore');
            const colRef = collection(Fire.store, 'workspaces', workspaceId, 'billing_documents');

            // 중복 체크: 같은 subscriptionId로 이미 유효한 청구서가 있는지 확인
            // (canceled 상태는 제외)
            const existingQuery = query(colRef, where('subscriptionId', '==', sub.id));
            const existingDocs = await getDocs(existingQuery);

            // canceled가 아닌 청구서만 검사
            const validDocs = existingDocs.docs.filter(doc =>
                doc.data().status !== 'canceled'
            );

            if (validDocs.length > 0) {
                const existingInvoice = validDocs[0].data();
                alert(t(i18n)`An invoice already exists for this subscription.\nInvoice #: ${existingInvoice.invoiceNumber || validDocs[0].id}\nStatus: ${existingInvoice.status}`);
                return;
            }

            const confirm = window.confirm(t(i18n)`Do you want to create an invoice for this subscription?`);
            if (!confirm) return;

            const now = new Date();
            const invoiceNumber = `INV-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;

            // 일본 총액표시 의무: sub.amount는 세금 포함 금액 (税込)
            // 내세 방식(内税): 합계에서 소비세를 역산
            const taxRate = 0.1; // 10%
            const total = sub.amount; // 세금 포함 총액
            const taxAmount = Math.floor(total - (total / (1 + taxRate))); // 소비세 역산
            const subtotal = total - taxAmount; // 세전 금액

            await addDoc(colRef, {
                type: 'INVOICE',
                invoiceNumber,
                workspaceId,
                subscriptionId: sub.id,
                subtotal,      // 세전 금액 (本体価格)
                taxRate,
                taxAmount,     // 소비세
                total,         // 세금 포함 총액 (税込合計)
                currency: sub.currency,
                issuer: {
                    name: 'Sunbi Soft',
                    registrationNumber: 'T0000000000000',
                    address: '東京都',
                    email: 'billing@sunbisoft.com',
                },
                recipient: {},
                items: [{
                    description: `${sub.planId} プランサブスクリプション`,
                    quantity: 1,
                    unitPrice: subtotal,  // 세전 단가
                    amount: subtotal,     // 세전 금액
                }],
                issueDate: serverTimestamp(),
                status: 'issued',
                notes: `구독 ID: ${sub.id}`,
                createdBy: 'super-admin',
                createdAt: serverTimestamp(),
            });

            alert(t(i18n)`Invoice has been created.`);
        } catch (error: any) {
            console.error('Failed to create invoice:', error);
            alert(t(i18n)`Failed to create invoice: ` + error.message);
        }
    };

    const formatDate = (date: Date) => date.toLocaleDateString('ja-JP');
    const formatCurrency = (amount: number, currency: string) => {
        return new Intl.NumberFormat('ja-JP', { style: 'currency', currency }).format(amount);
    };

    if (loading) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </CardContent>
            </Card>
        );
    }

    // Find current active subscription
    const currentSub = subscriptions.find(s => s.status === 'active');
    const scheduledSub = subscriptions.find(s => s.status === 'scheduled');

    return (
        <div className="space-y-6">
            {/* Current Subscription Summary Card */}
            <Card>
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-indigo-500" />
                            현재 구독 상태
                        </CardTitle>
                        {currentSub && (
                            <Badge className={cn(
                                "text-sm",
                                currentSub.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'
                            )}>
                                {currentSub.status}
                            </Badge>
                        )}
                    </div>
                </CardHeader>
                <CardContent>
                    {currentSub ? (
                        <div className="space-y-4">
                            {/* Plan & Price */}
                            <div className="flex items-baseline justify-between">
                                <span className="text-2xl font-bold text-indigo-600">{currentSub.planId}</span>
                                <div className="text-right">
                                    {currentSub.discount && currentSub.baseAmount > currentSub.amount && (
                                        <span className="text-sm text-slate-400 line-through mr-2">
                                            {formatCurrency(currentSub.baseAmount, currentSub.currency)}
                                        </span>
                                    )}
                                    <span className="text-xl font-bold">
                                        {formatCurrency(currentSub.amount, currentSub.currency)}
                                    </span>
                                    <span className="text-sm text-slate-500">/月</span>
                                </div>
                            </div>

                            {/* Discount/Promotion Info */}
                            {currentSub.discount && (
                                <div className="p-2 bg-emerald-50 rounded-lg text-sm text-emerald-700">
                                    {currentSub.discount.type === 'percent'
                                        ? `${currentSub.discount.value}% OFF`
                                        : `¥${currentSub.discount.value.toLocaleString()} OFF`
                                    }
                                    {currentSub.discount.reason && (
                                        <span className="text-emerald-600 ml-1">({currentSub.discount.reason})</span>
                                    )}
                                </div>
                            )}

                            {/* Details Grid */}
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div className="p-3 bg-slate-50 rounded-lg">
                                    <div className="text-slate-500 text-xs mb-1 flex items-center gap-1">
                                        <Calendar className="w-3 h-3" /> 시작일
                                    </div>
                                    <div className="font-medium">{formatDate(currentSub.startDate)}</div>
                                </div>
                                <div className="p-3 bg-slate-50 rounded-lg">
                                    <div className="text-slate-500 text-xs mb-1">종료일</div>
                                    <div className="font-medium">
                                        {currentSub.endDate ? formatDate(currentSub.endDate) : (
                                            <span className="text-emerald-600">無期限</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Reason */}
                            {currentSub.reason && (
                                <div className="text-sm text-slate-500">
                                    <span className="font-medium">변경 사유:</span> {currentSub.reason}
                                </div>
                            )}

                            {/* Scheduled Subscription Notice */}
                            {scheduledSub && (
                                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                    <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
                                        <Calendar className="w-4 h-4" />
                                        예약된 플랜 변경
                                    </div>
                                    <div className="mt-1 text-sm">
                                        <span className="font-bold text-amber-800">{scheduledSub.planId}</span>
                                        <span className="text-amber-600 ml-2">
                                            ({scheduledSub.scheduledFor ? formatDate(scheduledSub.scheduledFor) : '-'} 적용 예정)
                                        </span>
                                        {scheduledSub.amount > 0 && (
                                            <span className="ml-2">{formatCurrency(scheduledSub.amount, scheduledSub.currency)}</span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-slate-400 text-sm">활성 구독이 없습니다.</div>
                    )}
                </CardContent>
            </Card>

            {/* Subscription History Card */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <History className="w-5 h-5" />
                            <Trans>Subscription History</Trans>
                        </CardTitle>
                        <CardDescription>전체 구독 이력 및 관리</CardDescription>
                    </div>
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setEditedSubscription(defaultSubscriptionData())}>
                                <IconPlus className="w-4 h-4 mr-2" />
                                <Trans>Add Subscription</Trans>
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle><Trans>New Subscription</Trans></DialogTitle>
                                <DialogDescription>새로운 구독을 추가합니다.</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <Label><Trans>Plan</Trans></Label>
                                    <Select
                                        value={editedSubscription.planId}
                                        onValueChange={(v) => {
                                            const selectedTier = tiers.find(t => t.id === v);
                                            const monthlyPrice = selectedTier?.price?.monthly || 0;
                                            setEditedSubscription({
                                                ...editedSubscription,
                                                planId: v as WorkspaceTier,
                                                baseAmount: monthlyPrice,
                                                amount: monthlyPrice,
                                                currency: selectedTier?.price?.currency || 'JPY'
                                            });
                                        }}
                                    >
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {tiers.map(tier => (
                                                <SelectItem key={tier.id} value={tier.id}>
                                                    {tier.name} - ¥{tier.price?.monthly?.toLocaleString() || 0}/月
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Apply Timing - 먼저 보여주기 */}
                                <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                                    <Label className="text-xs font-semibold">적용 시점</Label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="radio" checked={applyImmediately} onChange={() => setApplyImmediately(true)} />
                                            <span className="text-sm">즉시 적용 (오늘부터)</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="radio" checked={!applyImmediately} onChange={() => setApplyImmediately(false)} />
                                            <span className="text-sm">다음 달 1일부터</span>
                                        </label>
                                    </div>
                                    <p className="text-xs text-slate-500">
                                        시작일: {applyImmediately
                                            ? new Date().toLocaleDateString('ja-JP')
                                            : `${new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toLocaleDateString('ja-JP')}`
                                        }
                                    </p>
                                </div>

                                {/* End Date Only */}
                                <div className="space-y-2">
                                    <Label>종료일 (선택사항)</Label>
                                    <Input
                                        type="date"
                                        value={editedSubscription.endDate?.toISOString().split('T')[0] || ''}
                                        onChange={(e) => setEditedSubscription({ ...editedSubscription, endDate: e.target.value ? new Date(e.target.value) : null })}
                                    />
                                    <p className="text-xs text-slate-500">비워두면 무기한</p>
                                </div>

                                {/* Price & Promotion */}
                                <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                                    <div className="space-y-2">
                                        <Label>기본 금액 (월)</Label>
                                        <div className="flex items-center gap-2">
                                            <span className="text-slate-500">¥</span>
                                            <Input
                                                type="number"
                                                className="w-32"
                                                value={editedSubscription.baseAmount || 0}
                                                onChange={(e) => {
                                                    const base = parseInt(e.target.value) || 0;
                                                    const finalAmount = selectedPromotion
                                                        ? applyPromotion(base, selectedPromotion)
                                                        : base;
                                                    setEditedSubscription({
                                                        ...editedSubscription,
                                                        baseAmount: base,
                                                        amount: finalAmount
                                                    });
                                                }}
                                            />
                                            <span className="text-slate-500">/月</span>
                                        </div>
                                    </div>

                                    {/* Promotion Selector */}
                                    <div className="space-y-2">
                                        <Label>프로모션 적용</Label>
                                        <Select
                                            value={selectedPromotion?.id || 'none'}
                                            onValueChange={(v) => {
                                                if (v === 'none') {
                                                    setSelectedPromotion(null);
                                                    setEditedSubscription({
                                                        ...editedSubscription,
                                                        amount: editedSubscription.baseAmount,
                                                        discount: null
                                                    });
                                                } else {
                                                    const promo = promotions.find(p => p.id === v);
                                                    if (promo && isPromotionValid(promo, editedSubscription.planId)) {
                                                        setSelectedPromotion(promo);
                                                        const finalAmount = applyPromotion(editedSubscription.baseAmount || 0, promo);
                                                        setEditedSubscription({
                                                            ...editedSubscription,
                                                            amount: finalAmount,
                                                            discount: {
                                                                type: promo.discountType,
                                                                value: promo.discountValue,
                                                                reason: promo.name,
                                                                validUntil: promo.validUntil
                                                            }
                                                        });
                                                    }
                                                }
                                            }}
                                        >
                                            <SelectTrigger><SelectValue placeholder="적용 안함" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">적용 안함</SelectItem>
                                                {promotions
                                                    .filter(p => isPromotionValid(p, editedSubscription.planId))
                                                    .map(promo => (
                                                        <SelectItem key={promo.id} value={promo.id}>
                                                            {promo.name} ({formatPromotionDiscount(promo)})
                                                        </SelectItem>
                                                    ))
                                                }
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Final Amount */}
                                    <div className="pt-2 border-t border-slate-200">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-slate-600">최종 금액</span>
                                            <div className="text-right">
                                                {selectedPromotion && (
                                                    <span className="text-xs text-slate-400 line-through mr-2">
                                                        ¥{editedSubscription.baseAmount?.toLocaleString()}
                                                    </span>
                                                )}
                                                <span className="text-lg font-bold text-indigo-600">
                                                    ¥{editedSubscription.amount?.toLocaleString() || 0}
                                                </span>
                                                <span className="text-sm text-slate-500">/月</span>
                                            </div>
                                        </div>
                                        {selectedPromotion && (
                                            <p className="text-xs text-emerald-600 mt-1">
                                                {formatPromotionDiscount(selectedPromotion)} 적용됨
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Options */}
                                <div className="space-y-3 pt-4 border-t">
                                    <div className="space-y-1">
                                        <Label className="text-xs">변경 이유 <span className="text-red-500">*</span></Label>
                                        <Input placeholder="예: 업그레이드 요청" value={changeReason} onChange={(e) => setChangeReason(e.target.value)} />
                                    </div>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={createInvoice} onChange={(e) => setCreateInvoice(e.target.checked)} />
                                        <span className="text-sm">청구서 자동 생성</span>
                                    </label>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setIsDialogOpen(false)}><Trans>Cancel</Trans></Button>
                                <Button onClick={handleSaveSubscription} className="bg-indigo-600 hover:bg-indigo-700" disabled={saving || !changeReason.trim()}>
                                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                                    <Trans>Save</Trans>
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </CardHeader>
                <CardContent>
                    {subscriptions.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                            <p>구독 이력이 없습니다.</p>
                            <p className="text-sm mt-1">"Add Subscription" 버튼을 눌러 첫 구독을 추가하세요.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left">Status</th>
                                        <th className="px-3 py-2 text-left">Plan</th>
                                        <th className="px-3 py-2 text-left">Period</th>
                                        <th className="px-3 py-2 text-right">Amount</th>
                                        <th className="px-3 py-2 text-left">Reason</th>
                                        <th className="px-3 py-2 text-center w-10">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subscriptions.map((sub) => (
                                        <tr key={sub.id} className="border-b hover:bg-slate-50">
                                            <td className="px-3 py-2">
                                                <Badge className={cn(
                                                    "text-xs capitalize",
                                                    sub.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                                                        sub.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                                                            'bg-slate-100 text-slate-500'
                                                )}>
                                                    {sub.status}
                                                </Badge>
                                                {sub.status === 'scheduled' && sub.scheduledFor && (
                                                    <div className="text-meta-sm text-blue-600 mt-0.5">
                                                        {formatDate(sub.scheduledFor)}~
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 font-medium">{sub.planId}</td>
                                            <td className="px-3 py-2 text-slate-600 text-xs">
                                                {formatDate(sub.startDate)}
                                                {sub.endDate && ` ~ ${formatDate(sub.endDate)}`}
                                            </td>
                                            <td className="px-3 py-2 text-right font-medium">
                                                {sub.amount > 0 ? formatCurrency(sub.amount, sub.currency) : '-'}
                                            </td>
                                            <td className="px-3 py-2 text-slate-500 text-xs max-w-[150px] truncate" title={sub.reason}>
                                                {sub.reason || '-'}
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                {(sub.status === 'active' || sub.status === 'scheduled') && (
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="icon" className="h-8 w-8">
                                                                <IconMoreHorizontal className="w-4 h-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            {sub.status === 'active' && sub.amount > 0 && (
                                                                <DropdownMenuItem onClick={() => handleCreateInvoice(sub)}>
                                                                    <FileText className="w-4 h-4 mr-2" />
                                                                    <Trans>Create Invoice</Trans>
                                                                </DropdownMenuItem>
                                                            )}
                                                            <DropdownMenuSeparator />
                                                            <DropdownMenuItem
                                                                onClick={() => handleCancelSubscription(sub)}
                                                                className="text-red-600"
                                                            >
                                                                <XCircle className="w-4 h-4 mr-2" />
                                                                {sub.status === 'active'
                                                                    ? <Trans>Cancel subscription (switch to FREE)</Trans>
                                                                    : <Trans>Cancel reservation</Trans>
                                                                }
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default WorkspaceSubscriptionTab;
